/**
 * La Tienda Del Padel — Cart Drawer
 * Manages all cart interactions: add-to-cart, quantity changes, removals,
 * section rendering, upsell recommendations, and free shipping bar.
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    if (!this.drawer) return;

    this.content = this.drawer.querySelector('.cart-drawer__content');
    this.freeShippingThreshold = parseFloat(this.drawer.dataset.freeShippingThreshold || '1000') * 100;
    this.isUpdating = false;
    this.focusTrapElements = [];
    this.previousActiveElement = null;

    this.init();
  }

  init() {
    this.bindOpenClose();
    this.bindCartEvents();
    this.interceptAddToCartForms();
    this.listenForCartOpenEvent();
  }

  /* =============================================
     OPEN / CLOSE
     ============================================= */

  bindOpenClose() {
    // Cart icon in header and in the mobile bottom nav opens the drawer
    // (intercept the link click; the href stays as the no-JS fallback)
    document.querySelectorAll('.site-header__action-btn--cart, [data-cart-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close buttons and overlay
    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach(el => {
      el.addEventListener('click', (e) => {
        // Don't close if clicking a link inside (like "Continue Shopping" that also closes)
        if (el.tagName === 'A' && el.href) {
          // Let the link navigate, but close the drawer
          this.close();
          return;
        }
        e.preventDefault();
        this.close();
      });
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.previousActiveElement = document.activeElement;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');

    // Dispatch custom event for WhatsApp widget coordination
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    // Focus trap: focus the close button
    requestAnimationFrame(() => {
      const closeBtn = this.drawer.querySelector('.cart-drawer__close');
      if (closeBtn) closeBtn.focus();
    });
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');

    // Dispatch custom event for WhatsApp widget coordination
    document.dispatchEvent(new CustomEvent('cart-drawer:close'));

    // Restore focus
    if (this.previousActiveElement && this.previousActiveElement.focus) {
      this.previousActiveElement.focus();
    }
  }

  // Listen for programmatic cart:open events (used by cart fallback page)
  listenForCartOpenEvent() {
    document.addEventListener('cart:open', () => this.open());
  }

  /* =============================================
     INTERCEPT ADD-TO-CART FORMS
     ============================================= */

  interceptAddToCartForms() {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!form.matches('form[action="/cart/add"], form[action*="/cart/add"]')) return;

      e.preventDefault();

      // Find the submit button for animation
      const submitBtn = form.querySelector('[type="submit"]');
      const formData = new FormData(form);

      this.addToCart(formData, submitBtn);
    });
  }

  /* =============================================
     ADD TO CART
     ============================================= */

  async addToCart(formData, submitBtn) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    // Disable the button while adding
    if (submitBtn) {
      submitBtn.disabled = true;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        this.showError(error.description || 'Could not add to cart');
        return;
      }

      const addData = await response.json();

      // GA4: add_to_cart event + Meta Pixel: AddToCart
      // Shopify's /cart/add.js returns the line's cumulative quantity (e.g. 3 if cart
      // already had 2 and we added 1). The form's `quantity` field is what was actually
      // added on this click, which is what analytics should record.
      {
        const addedQty = parseInt(formData.get('quantity') || '1', 10) || 1;
        const lines = Array.isArray(addData.items) ? addData.items : [addData];
        const items = lines.map(l => ({
          item_id: String(l.product_id || l.id),
          item_name: l.product_title || l.title,
          item_brand: l.vendor || undefined,
          item_category: l.product_type || undefined,
          item_variant: l.variant_title || undefined,
          price: (l.price || 0) / 100,
          quantity: addedQty
        }));
        const totalValue = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'add_to_cart', {
            currency: 'EGP',
            value: totalValue,
            items
          });
        }
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'AddToCart', {
            content_ids: items.map(i => i.item_id),
            content_type: 'product',
            content_name: items[0] && items[0].item_name,
            currency: 'EGP',
            value: totalValue
          });
        }
      }

      // Success animation on button
      if (submitBtn) {
        submitBtn.classList.add('btn--success');
        setTimeout(() => {
          submitBtn.classList.remove('btn--success');
        }, 1500);
      }

      // Bounce animation on header cart icon
      this.bounceCartIcon();

      // Refresh cart drawer content
      await this.refreshCart();

      // Open drawer after a short delay
      setTimeout(() => {
        this.open();
      }, 1000);

    } catch (err) {
      console.error('Add to cart error:', err);
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.isUpdating = false;
      if (submitBtn) {
        submitBtn.disabled = false;
      }
    }
  }

  /* =============================================
     QUANTITY CHANGE & REMOVE
     ============================================= */

  bindCartEvents() {
    // Quantity +/- buttons
    this.drawer.querySelectorAll('[data-quantity-change]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const lineKey = btn.dataset.lineKey;
        const quantity = parseInt(btn.dataset.quantity, 10);
        this.updateQuantity(lineKey, quantity);
      });
    });

    // Remove buttons
    this.drawer.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const lineKey = btn.dataset.lineKey;
        this.updateQuantity(lineKey, 0);
      });
    });

    // Upsell add-to-cart buttons (rendered by JS)
    this.drawer.querySelectorAll('[data-upsell-add]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (variantId) {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');
          this.addToCart(formData, btn);
        }
      });
    });

    // Re-bind close buttons inside drawer content (after refresh)
    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach(el => {
      // Remove previous listeners by cloning
      const newEl = el.cloneNode(true);
      el.parentNode.replaceChild(newEl, el);

      newEl.addEventListener('click', (e) => {
        if (newEl.tagName === 'A' && newEl.href) {
          this.close();
          return;
        }
        e.preventDefault();
        this.close();
      });
    });
  }

  async updateQuantity(lineKey, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    // Visual loading state on the line item
    const lineItem = this.drawer.querySelector(`[data-line-key="${lineKey}"]`);
    if (lineItem) {
      lineItem.classList.add('cart-drawer__item--loading');
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: quantity })
      });

      if (!response.ok) {
        console.error('Cart update failed');
        return;
      }

      await this.refreshCart();
    } catch (err) {
      console.error('Update quantity error:', err);
    } finally {
      this.isUpdating = false;
    }
  }

  /* =============================================
     REFRESH CART (Section Rendering API)
     ============================================= */

  async refreshCart() {
    try {
      const [sectionResponse, cartResponse] = await Promise.all([
        fetch('/?section_id=cart-drawer'),
        fetch('/cart.js')
      ]);

      const sectionHtml = await sectionResponse.text();
      const cart = await cartResponse.json();

      // Update drawer content
      const temp = document.createElement('div');
      temp.innerHTML = sectionHtml;
      const newContent = temp.querySelector('.cart-drawer__content');
      if (newContent) {
        this.content.innerHTML = newContent.innerHTML;
      }

      // Update data attribute for threshold
      const newDrawer = temp.querySelector('.cart-drawer');
      if (newDrawer && newDrawer.dataset.freeShippingThreshold) {
        this.freeShippingThreshold = parseFloat(newDrawer.dataset.freeShippingThreshold) * 100;
      }

      // Update all header cart count badges
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count > 0 ? cart.item_count : '';
        el.dataset.count = cart.item_count;
      });

      // Re-bind event listeners on new content
      this.bindCartEvents();

      // Load upsell recommendations
      this.loadRecommendations(cart);

    } catch (err) {
      console.error('Cart refresh error:', err);
    }
  }

  /* =============================================
     UPSELL RECOMMENDATIONS
     ============================================= */

  async loadRecommendations(cart) {
    const container = this.drawer.querySelector('[data-upsell-container]');
    const grid = this.drawer.querySelector('[data-upsell-grid]');
    if (!container || !grid) return;

    if (!cart.items || cart.items.length === 0) {
      container.hidden = true;
      return;
    }

    /* Strategy: surface products from the KEY CATEGORIES that the cart is
       MISSING (a setup needs racket + bag + balls + overgrip), in priority
       order. Only when fewer than 4 gap-fillers are found, top up with
       Shopify's generic complementary recommendation against the most
       expensive cart item. */
    const cartProductIds = cart.items.map(item => item.product_id);
    const cartTypes = new Set(
      cart.items.map(item => (item.product_type || '').toLowerCase().trim())
    );

    /* Map Shopify product_type → key category collection handle.

       The 5 buckets mirror the storefront nav (Rackets / Apparel / Bags /
       Balls / Accessories). "Bundles" is in the nav but not here — it's a
       packaging form, not a complement (recommending a bundle to a customer
       who already has a racket in cart is nonsensical). Overgrips fold into
       Accessories per merchant guidance. Shoes/footwear fold into Apparel
       (the nav has no separate footwear entry, customers think of them as
       apparel). */
    const typeToCategory = {
      racket: 'rackets',
      bundle: 'rackets',
      bag: 'bags',
      bags: 'bags',
      ball: 'balls',
      balls: 'balls',
      overgrip: 'accessories',
      overgrips: 'accessories',
      accessories: 'accessories',
      apparel: 'apparel',
      shoes: 'apparel',
      footwear: 'apparel',
    };

    const ownedCategories = new Set();
    cartTypes.forEach(t => {
      if (typeToCategory[t]) ownedCategories.add(typeToCategory[t]);
    });

    /* Priority order = highest-intent complements first. Rackets (highest
       AOV) → bags (next must-have) → balls (consumable) → accessories
       (overgrips/protectors) → apparel (softest add-on). */
    const keyCategories = ['rackets', 'bags', 'balls', 'accessories', 'apparel'];
    const missingCategories = keyCategories.filter(c => !ownedCategories.has(c));

    const recommendations = [];
    const seenIds = new Set(cartProductIds);

    /* /collections/<handle>/products.json returns a different shape than
       /recommendations/products.json. renderUpsellCard expects the recs-API
       shape (featured_image string, url string, variant.price as int cents).
       Normalize the collection-endpoint payload before pushing.

       Differences:
       - featured_image: not present on collection endpoint → derive from images[0]
       - url: not present on collection endpoint → build from handle
       - variant.price: decimal STRING ("4849.00") on collection endpoint vs
         integer cents (484900) on recs endpoint
       - variant.compare_at_price: same string vs cents difference */
    const normalizeCollectionProduct = p => {
      const firstImage =
        (p.images && p.images.length > 0)
          ? (typeof p.images[0] === 'string' ? p.images[0] : p.images[0].src)
          : null;
      return {
        ...p,
        url: p.url || (p.handle ? '/products/' + p.handle : '#'),
        featured_image: p.featured_image || firstImage,
        variants: (p.variants || []).map(v => {
          const out = { ...v };
          if (typeof v.price === 'string') {
            out.price = Math.round(parseFloat(v.price) * 100);
          }
          if (typeof v.compare_at_price === 'string') {
            out.compare_at_price = Math.round(parseFloat(v.compare_at_price) * 100);
          }
          return out;
        }),
      };
    };

    /* Per-day per-cart rotation seed.
       Hash of `cart.token + YYYY-MM-DD (UTC)` produces a stable integer
       offset that rotates which best-sellers the customer sees within the
       top-20 pool per missing category. Same customer, same day = same
       recs (consistent within a session). Next day = different starting
       slice. Across customers on the same day, the cart.token component
       diverges the offsets, so two carts with identical contents don't
       always see the same picks. Per UX consult: per-day NOT per-session
       — per-session feels twitchy when the customer reopens the drawer. */
    const cartToken = cart.token || '';
    const utcDate = new Date().toISOString().slice(0, 10);
    const seed = cartToken + '|' + utcDate;
    let seedHash = 0;
    for (let i = 0; i < seed.length; i++) {
      seedHash = ((seedHash << 5) - seedHash + seed.charCodeAt(i)) | 0;
    }
    const rotationOffset = Math.abs(seedHash);

    /* rotatedSlice — take `count` items from `arr` starting at `offset`,
       wrapping past the end. Returns [] for empty input. */
    const rotatedSlice = (arr, offset, count) => {
      if (!arr || arr.length === 0) return [];
      const len = arr.length;
      const start = ((offset % len) + len) % len;
      const out = [];
      for (let i = 0; i < count && i < len; i++) {
        out.push(arr[(start + i) % len]);
      }
      return out;
    };

    /* getRecommendationPool — single source-of-truth fetch for the
       per-category candidate pool. Today: BEST_SELLING top 20 from the
       category collection. Future curated-mode flip: branch here to read
       a `featured-this-week` collection (or per-category curated handle)
       when populated, falling back to best-sellers when empty. Keeping
       the seam here means no changes to the rotation/dedup/filter logic
       below if/when curation is wired in.

       IMPORTANT DEPENDENCY: this fetch returns the collection in its
       ADMIN-CONFIGURED sortOrder, NOT the order implied by any URL
       param. `/collections/<handle>/products.json?sort_by=...` is
       silently ignored — verified 2026-05-15 against rackets and
       new-arrivals; same response with and without sort_by, even for
       price-ascending. The 5 keyCategories collections (rackets / bags /
       balls / accessories / apparel) MUST be configured with
       `sortOrder: BEST_SELLING` in Shopify admin for these recs to be
       real best-sellers. Verified as of 2026-05-15. Do NOT add a
       sort_by= URL param expecting it to "fix" a wrong admin sort —
       it does nothing here. See CLAUDE.md > Known Issues. */
    const getRecommendationPool = async (handle) => {
      try {
        const resp = await fetch(`/collections/${handle}/products.json?limit=20`);
        if (!resp.ok) return [];
        const data = await resp.json();
        return data.products || [];
      } catch (err) {
        return [];
      }
    };

    /* Pass 1 — pull up to 2 items per missing category, rotated. */
    for (const handle of missingCategories) {
      if (recommendations.length >= 4) break;
      const pool = await getRecommendationPool(handle);
      const candidates = pool.filter(p => {
        if (seenIds.has(p.id)) return false;
        /* Require at least one available variant. */
        return p.variants && p.variants.some(v => v.available);
      });
      const picks = rotatedSlice(candidates, rotationOffset, 2);
      for (const c of picks) {
        recommendations.push(normalizeCollectionProduct(c));
        seenIds.add(c.id);
        if (recommendations.length >= 4) break;
      }
    }

    /* Pass 2 — top up via Shopify's complementary intent against the most
       expensive cart item if we still have fewer than 4. */
    if (recommendations.length < 4) {
      const anchorItem = cart.items.reduce(
        (best, item) => (!best || item.line_price > best.line_price ? item : best),
        null
      );
      const anchorId = anchorItem ? anchorItem.product_id : cart.items[0].product_id;
      try {
        const resp = await fetch(
          `/recommendations/products.json?product_id=${anchorId}&limit=8&intent=complementary`
        );
        if (resp.ok) {
          const data = await resp.json();
          const fallback = (data.products || []).filter(p => {
            if (seenIds.has(p.id)) return false;
            return p.variants && p.variants.some(v => v.available);
          });
          for (const f of fallback) {
            recommendations.push(f);
            seenIds.add(f.id);
            if (recommendations.length >= 4) break;
          }
        }
      } catch (err) {
        /* Fallback failed — proceed with whatever we already have. */
      }
    }

    if (recommendations.length === 0) {
      container.hidden = true;
      return;
    }

    grid.innerHTML = recommendations
      .slice(0, 4)
      .map(product => this.renderUpsellCard(product))
      .join('');
    container.hidden = false;

    grid.querySelectorAll('[data-upsell-add]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (variantId) {
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', '1');
          this.addToCart(formData, btn);
        }
      });
    });
  }

  renderUpsellCard(product) {
    const variant = product.variants && product.variants.length > 0
      ? product.variants.find(v => v.available) || product.variants[0]
      : null;

    const variantId = variant ? variant.id : '';
    const price = variant ? this.formatMoney(variant.price) : '';
    const imageUrl = product.featured_image
      ? this.getImageUrl(product.featured_image, '280x200')
      : '';
    const imageTag = imageUrl
      ? `<img class="cart-drawer__upsell-card-image" src="${imageUrl}" alt="${this.escapeHtml(product.title)}" width="140" height="100" loading="lazy">`
      : '';

    return `
      <div class="cart-drawer__upsell-card">
        <a href="${product.url}">
          ${imageTag}
        </a>
        <div class="cart-drawer__upsell-card-info">
          <a href="${product.url}" class="cart-drawer__upsell-card-title">${this.escapeHtml(product.title)}</a>
          <div class="cart-drawer__upsell-card-price">${price}</div>
          ${variant && variant.available
            ? `<button class="cart-drawer__upsell-card-btn" type="button" data-upsell-add data-variant-id="${variantId}">+ Add</button>`
            : `<span class="cart-drawer__upsell-card-btn" style="opacity:0.4;cursor:default">Sold Out</span>`
          }
        </div>
      </div>
    `;
  }

  /* =============================================
     HEADER CART ICON BOUNCE
     ============================================= */

  bounceCartIcon() {
    const cartBtn = document.querySelector('.site-header__action-btn--cart');
    if (!cartBtn) return;
    cartBtn.classList.add('cart-icon--bounce');
    setTimeout(() => {
      cartBtn.classList.remove('cart-icon--bounce');
    }, 600);
  }

  /* =============================================
     ERROR DISPLAY
     ============================================= */

  showError(message) {
    // Simple alert for now — can be replaced with a toast notification
    console.error('Cart error:', message);
  }

  /* =============================================
     UTILITY FUNCTIONS
     ============================================= */

  formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    const amount = (cents / 100).toFixed(2);
    // Remove trailing .00
    const formatted = amount.replace(/\.00$/, '');
    return `EGP ${formatted}`;
  }

  getImageUrl(src, size) {
    // Handle Shopify CDN image URLs
    if (typeof src === 'string' && src.includes('cdn.shopify.com')) {
      return src.replace(/(\.[a-z]+)(\?|$)/, `_${size}$1$2`);
    }
    // For recommendation API images, use the size parameter approach
    if (typeof src === 'object' && src.src) {
      return src.src;
    }
    return src || '';
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});

/* Shared entry point for "add to cart happened out-of-band — please refresh the
   drawer (line items + header count + recommendations rail) and open it."
   Used by frequently-bought-together.js, product-comparison.js, and
   quick-view.js. Awaits refresh before opening so the drawer never appears
   with stale contents even on slow connections. Falls back to a cart:open
   dispatch if the drawer module hasn't loaded for some reason. */
window.refreshCartDrawer = async function refreshCartDrawer() {
  const drawer = window.cartDrawer;
  if (drawer && typeof drawer.refreshCart === 'function') {
    await drawer.refreshCart();
    if (typeof drawer.open === 'function') drawer.open();
    return;
  }
  document.dispatchEvent(new CustomEvent('cart:open'));
};
